import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '@/App';
import {
  X,
  ArrowRight,
  AlertCircle,
  CheckCircle,
  CreditCard,
  Wallet,
  Building,
  Smartphone,
  TrendingUp,
  DollarSign
} from 'lucide-react';
import { showToast } from '@/lib/custom-toast';
import { formatCurrencyInput, parseCurrency } from '@/utils/currencyFormat';

import BACKEND_URL from '@/config/backendUrl';


const ACCOUNT_TYPE_ICONS = {
  bank: Building,
  cash: Wallet,
  credit_card: CreditCard,
  digital_wallet: Smartphone,
  investment: TrendingUp
};

const TransferModal = ({ isOpen, onClose, onSuccess, preselectedAccountId = null }) => {
  const { token } = useContext(AuthContext);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  
  const [transferData, setTransferData] = useState({
    source_account_id: preselectedAccountId || '',
    destination_account_id: '',
    amount: '',
    description: ''
  });
  
  const [validation, setValidation] = useState({
    hasError: false,
    message: '',
    type: '' // 'error', 'warning', 'info'
  });
  
  const [creditStatus, setCreditStatus] = useState({
    source: null,
    destination: null
  });

  // Touch Drag-Down State (Mobile bottom-sheet)
  const [dragStartY, setDragStartY] = useState(0);
  const [dragCurrentY, setDragCurrentY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const isFromHandle = useRef(false);

  // Lock Body Scroll when Open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Fetch accounts
  useEffect(() => {
    if (isOpen && token) {
      fetchAccounts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, token]);

  // Update source account when preselected changes
  useEffect(() => {
    if (preselectedAccountId) {
      setTransferData(prev => ({ ...prev, source_account_id: preselectedAccountId }));
    }
  }, [preselectedAccountId]);

  // Validate transfer when data changes
  useEffect(() => {
    if (transferData.source_account_id && transferData.destination_account_id && transferData.amount) {
      validateTransfer();
    } else {
      setValidation({ hasError: false, message: '', type: '' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferData.source_account_id, transferData.destination_account_id, transferData.amount]);

  // Fetch credit status for credit cards
  useEffect(() => {
    if (transferData.source_account_id) {
      const sourceAccount = accounts.find(a => a.id === transferData.source_account_id);
      if (sourceAccount && sourceAccount.type === 'credit_card') {
        fetchCreditStatus(transferData.source_account_id, 'source');
      } else {
        setCreditStatus(prev => ({ ...prev, source: null }));
      }
    }
    
    if (transferData.destination_account_id) {
      const destAccount = accounts.find(a => a.id === transferData.destination_account_id);
      if (destAccount && destAccount.type === 'credit_card') {
        fetchCreditStatus(transferData.destination_account_id, 'destination');
      } else {
        setCreditStatus(prev => ({ ...prev, destination: null }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferData.source_account_id, transferData.destination_account_id, accounts]);

  const fetchAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const response = await fetch(`${BACKEND_URL}/api/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const sortedData = [...data].sort((a, b) => {
          if (a.is_default && !b.is_default) return -1;
          if (!a.is_default && b.is_default) return 1;
          return (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' });
        });
        setAccounts(sortedData);
        
        // Si no hay cuenta preseleccionada, seleccionar la cuenta predeterminada automáticamente
        if (!preselectedAccountId && !transferData.source_account_id) {
          const defaultAccount = data.find(acc => acc.is_default);
          if (defaultAccount) {
            setTransferData(prev => ({
              ...prev,
              source_account_id: defaultAccount.id
            }));
          }
        }
      } else {
        showToast.error('Error al cargar las cuentas');
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      showToast.error('Error de conexión al cargar cuentas');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchCreditStatus = async (accountId, type) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/accounts/${accountId}/credit-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCreditStatus(prev => ({ ...prev, [type]: data }));
      }
    } catch (error) {
      console.error('Error fetching credit status:', error);
    }
  };

  const validateTransfer = () => {
    const sourceAccount = accounts.find(a => a.id === transferData.source_account_id);
    const destAccount = accounts.find(a => a.id === transferData.destination_account_id);
    const amount = parseFloat(parseCurrency(transferData.amount));

    if (!sourceAccount || !destAccount) return;

    // Validar que no sean la misma cuenta
    if (transferData.source_account_id === transferData.destination_account_id) {
      setValidation({
        hasError: true,
        message: 'No puedes transferir a la misma cuenta',
        type: 'error'
      });
      return;
    }

    // Validar monto positivo
    if (amount <= 0) {
      setValidation({
        hasError: true,
        message: 'El monto debe ser mayor a cero',
        type: 'error'
      });
      return;
    }

    // Validar fondos suficientes para cuentas normales
    if (sourceAccount.type !== 'credit_card') {
      if (amount > sourceAccount.current_balance) {
        setValidation({
          hasError: true,
          message: `Fondos insuficientes en ${sourceAccount.name}. Disponible: ${formatCurrency(sourceAccount.current_balance, sourceAccount.currency)}`,
          type: 'error'
        });
        return;
      }
    }

    // Validar crédito disponible para tarjetas de crédito
    if (sourceAccount.type === 'credit_card') {
      const availableCredit = (sourceAccount.credit_limit || 0) + sourceAccount.current_balance; // current_balance es negativo
      if (amount > availableCredit) {
        setValidation({
          hasError: true,
          message: `Crédito insuficiente en ${sourceAccount.name}. Disponible: ${formatCurrency(availableCredit, sourceAccount.currency)}`,
          type: 'error'
        });
        return;
      }

      // Advertencia si usa más del 80% del crédito
      const newBalance = sourceAccount.current_balance - amount;
      const usagePercent = (Math.abs(newBalance) / sourceAccount.credit_limit) * 100;
      if (usagePercent > 80) {
        setValidation({
          hasError: false,
          message: `⚠️ Después de esta transferencia, usarás ${usagePercent.toFixed(1)}% de tu límite de crédito`,
          type: 'warning'
        });
        return;
      }
    }

    // Transferencia válida - mostrar información
    if (destAccount.type === 'credit_card') {
      setValidation({
        hasError: false,
        message: `💳 Esta transferencia reducirá la deuda de ${destAccount.name}`,
        type: 'info'
      });
    } else if (sourceAccount.type === 'credit_card') {
      setValidation({
        hasError: false,
        message: `💳 Esta transferencia creará un cargo en ${sourceAccount.name}`,
        type: 'info'
      });
    } else {
      setValidation({
        hasError: false,
        message: '✅ Transferencia válida',
        type: 'info'
      });
    }
  };

  const formatCurrency = (value, currency) => {
    const symbol = currency === 'USD' ? '$' : '$';
    return `${symbol}${parseFloat(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleInputChange = (field, value) => {
    if (field === 'amount') {
      // Guardar el valor limpio (sin formato) en el estado
      const cleaned = parseCurrency(value);
      setTransferData(prev => ({ ...prev, [field]: cleaned }));
    } else {
      setTransferData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validation.hasError) {
      showToast.error('Por favor corrige los errores antes de continuar');
      return;
    }

    if (!transferData.source_account_id || !transferData.destination_account_id || !transferData.amount) {
      showToast.error('Por favor completa todos los campos requeridos');
      return;
    }

    try {
      setLoading(true);
      
      const today = new Date();
      const localDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      const response = await fetch(`${BACKEND_URL}/api/transfers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from_account_id: transferData.source_account_id,
          to_account_id: transferData.destination_account_id,
          amount: parseFloat(parseCurrency(transferData.amount)),
          description: transferData.description || 'Transferencia entre cuentas',
          date: localDate
        })
      });

      if (response.ok) {
        const data = await response.json();
        showToast.success(data.message || 'Transferencia realizada exitosamente');
        handleClose();
        if (onSuccess) onSuccess();
      } else {
        const data = await response.json().catch(() => ({}));
        const rawDetail = data?.detail;
        let errorMsg = 'Error al realizar la transferencia';
        if (typeof rawDetail === 'string') {
          errorMsg = rawDetail;
        } else if (Array.isArray(rawDetail)) {
          errorMsg = rawDetail.map(err => err.msg || err.message || 'Dato inválido').join(', ');
        } else if (rawDetail && typeof rawDetail === 'object') {
          errorMsg = rawDetail.msg || rawDetail.message || 'Error en los datos de la transferencia';
        }
        showToast.error(errorMsg);
      }
    } catch (error) {
      console.error('Error creating transfer:', error);
      showToast.error('Error de conexión al realizar transferencia');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTransferData({
      source_account_id: preselectedAccountId || '',
      destination_account_id: '',
      amount: '',
      description: ''
    });
    setValidation({ hasError: false, message: '', type: '' });
    setCreditStatus({ source: null, destination: null });
    onClose();
  };

  const getAccountIcon = (type) => {
    const IconComponent = ACCOUNT_TYPE_ICONS[type] || Wallet;
    return IconComponent;
  };

  const getCreditStatusColor = (alertLevel) => {
    switch (alertLevel) {
      case 'critical': return 'text-red-600';
      case 'warning': return 'text-yellow-600';
      case 'info': return 'text-blue-600';
      default: return 'text-green-600';
    }
  };

  if (!isOpen) return null;

  const sourceAccount = accounts.find(a => a.id === transferData.source_account_id);
  const destAccount = accounts.find(a => a.id === transferData.destination_account_id);

  const handleTouchStart = (e, fromHandle = false) => {
    isFromHandle.current = fromHandle;
    setDragStartY(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const diff = e.touches[0].clientY - dragStartY;
    if (diff > 0) {
      setDragCurrentY(diff);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    const threshold = isFromHandle.current ? 75 : 190;
    if (dragCurrentY > threshold) {
      handleClose();
    }
    setDragCurrentY(0);
    isFromHandle.current = false;
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        style={{
          transform: isDragging && dragCurrentY > 0 ? `translateY(${dragCurrentY}px)` : undefined,
          transition: isDragging ? 'none' : 'transform 0.2s ease-out'
        }}
        className="bg-white dark:bg-slate-800 rounded-t-[2.2rem] sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl flex flex-col max-h-[92vh] sm:max-h-[calc(100vh-4rem)] overflow-hidden"
      >
        {/* Drag Bar (Mobile) */}
        <div
          onTouchStart={(e) => handleTouchStart(e, true)}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="sm:hidden pt-3 pb-2 cursor-grab active:cursor-grabbing flex justify-center shrink-0 select-none bg-white dark:bg-slate-800"
        >
          <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full" />
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 sm:px-6 py-3.5 sm:py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center text-white">
              <ArrowRight className="w-4 h-4" />
            </div>
            <h2 className="text-base sm:text-xl font-bold text-white">Nueva Transferencia</h2>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            type="button"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Body - Scrollable */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
          {/* Source Account */}
          <div>
            <label className="block text-sm sm:text-base font-medium text-slate-700 dark:text-slate-300 mb-2">
              Cuenta de Origen
            </label>
            <select
              value={transferData.source_account_id}
              onChange={(e) => handleInputChange('source_account_id', e.target.value)}
              className="w-full px-3 sm:px-4 py-3 sm:py-3 text-base border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-700 dark:text-white"
              required
              disabled={loadingAccounts}
            >
              <option value="">Seleccionar cuenta...</option>
              {accounts.map(account => {
                // eslint-disable-next-line no-unused-vars
                const IconComponent = getAccountIcon(account.type);
                return (
                  <option key={account.id} value={account.id}>
                    {account.name} - {formatCurrency(account.current_balance, account.currency)} {account.currency}
                  </option>
                );
              })}
            </select>
            
            {/* Source Account Credit Status */}
            {sourceAccount && sourceAccount.type === 'credit_card' && creditStatus.source && (
              <div className={`mt-2 p-2 sm:p-3 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700`}>
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <CreditCard className={`w-4 h-4 ${getCreditStatusColor(creditStatus.source.alert_level)}`} />
                  <span className={`font-medium ${getCreditStatusColor(creditStatus.source.alert_level)}`}>
                    {creditStatus.source.alert_message}
                  </span>
                </div>
                <div className="mt-1 sm:mt-2 text-xs text-slate-600 dark:text-slate-400">
                  Crédito disponible: {formatCurrency((sourceAccount.credit_limit || 0) + sourceAccount.current_balance, sourceAccount.currency)}
                </div>
              </div>
            )}
          </div>

          {/* Arrow Indicator */}
          <div className="flex justify-center -my-2 sm:-my-3">
            <div className="p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-full">
              <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>

          {/* Destination Account */}
          <div>
            <label className="block text-sm sm:text-base font-medium text-slate-700 dark:text-slate-300 mb-2">
              Cuenta de Destino
            </label>
            <select
              value={transferData.destination_account_id}
              onChange={(e) => handleInputChange('destination_account_id', e.target.value)}
              className="w-full px-3 sm:px-4 py-3 sm:py-3 text-base border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-700 dark:text-white"
              required
              disabled={loadingAccounts}
            >
              <option value="">Seleccionar cuenta...</option>
              {accounts.reduce((acc, account) => {
                if (account.id !== transferData.source_account_id) {
                  acc.push(
                    <option key={account.id} value={account.id}>
                      {account.name} - {formatCurrency(account.current_balance, account.currency)} {account.currency}
                    </option>
                  );
                }
                return acc;
              }, [])}
            </select>
            
            {/* Destination Account Credit Status */}
            {destAccount && destAccount.type === 'credit_card' && creditStatus.destination && (
              <div className="mt-2 p-3 sm:p-4 rounded-xl bg-slate-50 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs sm:text-sm">
                    <CreditCard className={`w-4 h-4 ${getCreditStatusColor(creditStatus.destination.alert_level)}`} />
                    <span className={`font-semibold ${getCreditStatusColor(creditStatus.destination.alert_level)}`}>
                      {creditStatus.destination.alert_message}
                    </span>
                  </div>
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    Deuda: {formatCurrency(creditStatus.destination.current_debt || Math.abs(destAccount.current_balance), destAccount.currency)}
                  </span>
                </div>

                {/* Resumen Cuotas / Contado si existen */}
                {(creditStatus.destination.msi_month_payment > 0 || creditStatus.destination.non_msi_debt > 0) && (
                  <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-500 dark:text-slate-400 pt-1 border-t border-slate-200/60 dark:border-slate-800">
                    <div>
                      <span>Compras contado:</span>{' '}
                      <strong className="text-slate-700 dark:text-slate-200">{formatCurrency(creditStatus.destination.non_msi_debt || 0, destAccount.currency)}</strong>
                    </div>
                    <div>
                      <span>MSI este mes:</span>{' '}
                      <strong className="text-slate-700 dark:text-slate-200">{formatCurrency(creditStatus.destination.msi_month_payment || 0, destAccount.currency)}</strong>
                    </div>
                  </div>
                )}

                {/* Botones de Auto-llenar Monto */}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {creditStatus.destination.suggested_monthly_payment > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setTransferData(prev => ({
                          ...prev,
                          amount: creditStatus.destination.suggested_monthly_payment.toString(),
                          description: prev.description || `Pago del mes ${destAccount.name}`
                        }));
                      }}
                      className="px-2.5 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-300 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 text-xs font-bold hover:bg-emerald-100 transition-colors flex items-center gap-1"
                    >
                      <span>💡 Pagar Mes:</span>
                      <strong>{formatCurrency(creditStatus.destination.suggested_monthly_payment, destAccount.currency)}</strong>
                    </button>
                  )}
                  {(creditStatus.destination.current_debt > 0 || Math.abs(destAccount.current_balance) > 0) && (
                    <button
                      type="button"
                      onClick={() => {
                        const total = creditStatus.destination.current_debt || Math.abs(destAccount.current_balance);
                        setTransferData(prev => ({
                          ...prev,
                          amount: total.toString(),
                          description: prev.description || `Liquidación total ${destAccount.name}`
                        }));
                      }}
                      className="px-2.5 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-300 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 text-xs font-bold hover:bg-indigo-100 transition-colors flex items-center gap-1"
                    >
                      <span>💳 Liquidar Total:</span>
                      <strong>{formatCurrency(creditStatus.destination.current_debt || Math.abs(destAccount.current_balance), destAccount.currency)}</strong>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm sm:text-base font-medium text-slate-700 dark:text-slate-300 mb-2">
              Monto
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                inputMode="decimal"
                value={transferData.amount ? formatCurrencyInput(transferData.amount) : ''}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                placeholder="0.00"
                className="w-full pl-10 pr-4 py-3 text-base sm:text-lg border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-700 dark:text-white font-medium"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm sm:text-base font-medium text-slate-700 dark:text-slate-300 mb-2">
              Descripción (opcional)
            </label>
            <input
              type="text"
              value={transferData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Ej: Pago de tarjeta de crédito"
              className="w-full px-3 sm:px-4 py-3 text-base border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-slate-700 dark:text-white"
            />
          </div>

          {/* Validation Message */}
          {validation.message && (
            <div className={`p-3 sm:p-4 rounded-lg flex items-start gap-2 sm:gap-3 ${
              validation.type === 'error' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' :
              validation.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' :
              'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
            }`}>
              {validation.type === 'error' ? (
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              ) : validation.type === 'warning' ? (
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              ) : (
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              )}
              <p className={`text-xs sm:text-sm ${
                validation.type === 'error' ? 'text-red-800 dark:text-red-200' :
                validation.type === 'warning' ? 'text-yellow-800 dark:text-yellow-200' :
                'text-blue-800 dark:text-blue-200'
              }`}>
                {validation.message}
              </p>
            </div>
          )}

          {/* Transfer Summary */}
          {sourceAccount && destAccount && transferData.amount && !validation.hasError && (
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-3 sm:p-4 rounded-lg border border-slate-200 dark:border-slate-700">
              <h3 className="text-sm sm:text-base font-semibold text-slate-700 dark:text-slate-300 mb-2 sm:mb-3">Resumen de Transferencia</h3>
              <div className="space-y-2 text-xs sm:text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-slate-600 dark:text-slate-400">Desde:</span>
                  <span className="font-medium text-slate-900 dark:text-white text-right">{sourceAccount.name}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-slate-600 dark:text-slate-400">Hacia:</span>
                  <span className="font-medium text-slate-900 dark:text-white text-right">{destAccount.name}</span>
                </div>
                <div className="flex justify-between gap-2 pt-2 border-t border-slate-300 dark:border-slate-600">
                  <span className="text-slate-600 dark:text-slate-400">Monto:</span>
                  <span className="font-bold text-base sm:text-lg text-blue-600 dark:text-blue-400">
                    {formatCurrency(parseCurrency(transferData.amount), sourceAccount.currency)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2 sm:pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="w-full sm:flex-1 px-4 sm:px-6 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors font-medium text-sm sm:text-base"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || validation.hasError || loadingAccounts}
              className="w-full sm:flex-1 px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {loading ? 'Procesando...' : 'Realizar Transferencia'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransferModal;
